import type { FC, PropsWithChildren } from "react";

import { BasicDialog } from "@repo/ui";
import { createContext, useCallback, useContext, useState } from "react";

import { useBackgroundCheckQueries } from "../../api/backgroundCheckApi";
import { useDrugTestApi } from "../../api/drugTestApi";
import { useRepContext } from "../../contexts/editableItemContext";
import { AddSegmentWizard } from "./segmentWizard";

export function useSegmentWizardData() {
	const { repId } = useRepContext();

	// Background check queries
	const { useBackgroundCheckQuery } = useBackgroundCheckQueries();
	const backgroundCheckQuery = useBackgroundCheckQuery();
	const backgroundChecks = backgroundCheckQuery?.data;

	// Drug test queries
	const { useDrugTestQuery } = useDrugTestApi();
	const drugTestQuery = useDrugTestQuery(repId);
	const drugTests = drugTestQuery?.data;

	return {
		backgroundChecks,
		backgroundCheckQuery,
		drugTests,
		drugTestQuery,
	};
}

interface SegmentWizardContextType {
	openSegmentWizard: (ids: number | number[], title: string) => void;
}

const SegmentWizardContext = createContext<SegmentWizardContextType | undefined>(undefined);

export const SegmentWizardProvider: FC<PropsWithChildren> = ({ children }) => {
	const { backgroundChecks, backgroundCheckQuery, drugTests, drugTestQuery } = useSegmentWizardData();

	const [isOpen, setIsOpen] = useState(false);
	const [activeIds, setActiveIds] = useState<number[]>([]);
	const [activeTitle, setActiveTitle] = useState<string>("");

	const openSegmentWizard = useCallback(
		(ids: number | number[], title: string) => {
			if (backgroundChecks.any() || drugTests.any()) {
				const idArray = Array.isArray(ids) ? ids : [ids];
				setActiveIds(idArray);
				setActiveTitle(title);
				setIsOpen(true);
			}
		},
		[backgroundChecks, drugTests],
	);

	return (
		<SegmentWizardContext.Provider value={{ openSegmentWizard }}>
			{children}
			<BasicDialog
				title={activeIds.length > 1 ? `Update ${activeTitle}` : `Add ${activeTitle}`}
				open={isOpen}
				onOpenChange={setIsOpen}
				hideTrigger
				fullWidth={false}
				className="md:min-w-[800px] md:max-w-[1200px]"
			>
				{activeIds.length > 0 && (
					<AddSegmentWizard
						newIds={activeIds}
						backgroundChecks={backgroundChecks}
						backgroundChecksLoaded={backgroundCheckQuery.loaded}
						drugTests={drugTests}
						drugTestsLoaded={drugTestQuery.loaded}
					/>
				)}
			</BasicDialog>
		</SegmentWizardContext.Provider>
	);
};

export const useSegmentWizard = () => {
	const context = useContext(SegmentWizardContext);
	if (context === undefined) {
		throw new Error("useSegmentWizard must be used within a SegmentWizardProvider");
	}
	return context;
};
